import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Building2, Plus, Pencil, Trash2, Star, RefreshCw } from "lucide-react";
import ListingFormModal from "./ListingFormModal";

const PINK = "#4A6DC7";

export default function ListingsAdmin() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    base44.auth.me().then(u => {
      if (!u || u.role !== "admin") { navigate("/", { replace: true }); return; }
      load();
    }).catch(() => navigate("/", { replace: true }));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const all = await base44.entities.Listing.list("-created_date", 100);
      setListings(all);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const onSaved = async () => { setShowForm(false); setEditing(null); await load(); };

  const onDelete = async (l) => {
    if (!confirm(`Delete "${l.name}"?`)) return;
    try { await base44.entities.Listing.delete(l.id); } catch (e) { /* ignore */ }
    load();
  };

  const toggleFeatured = async (l) => {
    try { await base44.entities.Listing.update(l.id, { featured: !l.featured }); } catch (e) { /* ignore */ }
    load();
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F7F7F5", paddingTop: 88, paddingBottom: 60, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 clamp(16px, 5vw, 40px)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <Building2 size={20} color={PINK} />
              <h1 style={{ fontSize: 24, fontWeight: 800, color: "#1A1A1A", margin: 0 }}>Listings</h1>
            </div>
            <p style={{ fontSize: 13, color: "#888", margin: 0 }}>Manage listings shown in the homepage "New listings" section</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={load} style={{ display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1px solid #E5E5E5", borderRadius: 8, padding: "9px 16px", fontSize: 13, color: "#555", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}><RefreshCw size={13} /> Refresh</button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ display: "flex", alignItems: "center", gap: 6, background: PINK, border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, color: "#fff", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}><Plus size={14} /> Add listing</button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: "#ccc", fontSize: 14 }}>Loading…</p>
        ) : listings.length === 0 ? (
          <div style={{ background: "#fff", border: "1px dashed #ddd", borderRadius: 14, padding: 48, textAlign: "center" }}>
            <p style={{ color: "#888", fontSize: 14, margin: "0 0 16px" }}>No listings yet</p>
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#112215", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Plus size={14} /> Add your first listing</button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {listings.map(l => (
              <div key={l.id} style={{ background: "#fff", border: "1px solid #EBEBEB", borderRadius: 14, overflow: "hidden" }}>
                <div style={{ position: "relative", aspectRatio: "4 / 3", background: "#F4F2F0" }}>
                  {l.images?.[0] ? <img src={l.images[0]} alt={l.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#bbb", fontSize: 12 }}>No image</div>}
                  <button onClick={() => toggleFeatured(l)} title="Toggle featured" style={{ position: "absolute", top: 8, left: 8, background: l.featured ? PINK : "rgba(255,255,255,0.92)", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Star size={14} color={l.featured ? "#fff" : "#888"} fill={l.featured ? PINK : "none"} /></button>
                </div>
                <div style={{ padding: "14px 16px" }}>
                  <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, color: "#1A1A1A", margin: "0 0 2px" }}>{l.name}</h3>
                  <p style={{ fontSize: 12, color: "#888", margin: "0 0 10px" }}>{l.location || "—"}</p>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: "#555", marginBottom: 12 }}>
                    {l.price != null && <span>£{l.price}/night</span>}
                    {l.guests != null && <span>{l.guests} guests</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { setEditing(l); setShowForm(true); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#F7F7F5", border: "1px solid #eee", borderRadius: 8, padding: "8px", fontSize: 13, color: "#333", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}><Pencil size={13} /> Edit</button>
                    <button onClick={() => onDelete(l)} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#E8EDFF", border: "1px solid #f8d0d8", borderRadius: 8, padding: "8px 12px", cursor: "pointer" }}><Trash2 size={13} color={PINK} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {showForm && <ListingFormModal listing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />}
    </div>
  );
}